import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createClient } from 'graphql-ws';
import type { DocumentNode } from 'graphql';
import config, { getGraphqlUrl, getSubscriptionsUrl } from '../config/config';
import { AuthorizationMiddleware } from '../network/authorization-middleware';
import { TimezoneMiddleware } from '../network/timezone-middleware';
import { AppVersionMiddleware } from '../network/app-version-middleware';
import fragmentTypes from '../network/fragmentTypes.json';

let apolloClient: ApolloClient | null = null;

function buildAuthHeaders(): Record<string, string> {
  return {
    ...AuthorizationMiddleware.getHeader(),
    ...TimezoneMiddleware.getHeader(),
    ...AppVersionMiddleware.getHeader(),
  };
}

export function createApolloClient(): ApolloClient {
  const httpLink = new HttpLink({
    uri: getGraphqlUrl(config),
  });

  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...buildAuthHeaders(),
      },
    }));
    return forward(operation);
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: getSubscriptionsUrl(config),
      connectionParams: () => {
        const token = AuthorizationMiddleware.token;
        return {
          'accounts-access-token': token,
          authToken: token,
          'UTC-Offset': TimezoneMiddleware.offset,
          AppVersion: AppVersionMiddleware.version,
        };
      },
      retryAttempts: 5,
      shouldRetry: () => true,
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    authLink.concat(httpLink),
  );

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        console.error('[GraphQL error]', err.message);
      }
    }
    if (networkError) {
      console.error('[Network error]', networkError);
    }
  });

  return new ApolloClient({
    link: ApolloLink.from([errorLink, splitLink]),
    cache: new InMemoryCache({
      possibleTypes: fragmentTypes.__schema.types.reduce(
        (acc: Record<string, string[]>, type: { kind: string; name: string; possibleTypes?: { name: string }[] }) => {
          if (type.kind === 'UNION' || type.kind === 'INTERFACE') {
            acc[type.name] = type.possibleTypes?.map((t) => t.name) ?? [];
          }
          return acc;
        },
        {},
      ),
    }),
    defaultOptions: {
      watchQuery: { fetchPolicy: 'cache-and-network' },
      query: { fetchPolicy: 'network-only' },
    },
  });
}

export function initClient(): ApolloClient {
  if (!apolloClient) {
    apolloClient = createApolloClient();
  }
  return apolloClient;
}

export function getClient(): ApolloClient {
  if (!apolloClient) {
    return initClient();
  }
  return apolloClient;
}

/** @deprecated use getClient() */
export const client = new Proxy({} as ApolloClient, {
  get(_target, prop) {
    return Reflect.get(getClient(), prop);
  },
});

export function resetClient(): void {
  apolloClient = null;
}

export type { DocumentNode };
